// Externals
import type { Reducer } from 'redux';

// Libraries
import { START_ROLE_ASSUMPTION } from '@makemydeal/dr-activities-role-assumption';
import { bootstrapActionTypes, initActionTypes } from '@makemydeal/dr-shared-store';
import { DeepLinks, salesCheckInMode } from '@makemydeal/dr-sales-checkin-types';

// Types
import type { CommonState } from './types/common';

// Actions
import { Actions } from '../actionTypes/actionTypes';
import { getInitialCommonState } from './commonInitialState';

// Consts
import { ErrorMessage, ErrorType } from '@makemydeal/dr-sales-checkin-types';
import { SEND_TO_DESK_SUCCESS_MESSAGE } from '../activeDeal/constants';
import { getUrlSearchParams, SALES_CHECKIN_MODE } from '@makemydeal/dr-sales-checkin-ui-utils';

export const common: Reducer<CommonState> = (state: CommonState = { ...getInitialCommonState() }, action: any) => {
    const { payload, type } = action;

    switch (type) {
        case Actions.VIEW_CUSTOMER_INFO:
        case Actions.ADD_CO_BUYER:
        case Actions.SET_CUSTOMER_CARD_STATE: {
            return {
                ...state,
                customerCardState: payload.customerCardState,
                customerCardOptions: payload.options
            };
        }

        case Actions.SHOW_GLOBAL_SPINNER: {
            return {
                ...state,
                globalSpinnerCount: (state.globalSpinnerCount || 0) + 1,
                globalSpinnerMessage: payload?.message ?? state.globalSpinnerMessage
            };
        }

        case Actions.HIDE_GLOBAL_SPINNER:
        case Actions.DEALS_FETCHED: {
            const globalSpinnerCount = Math.max(0, (state.globalSpinnerCount || 0) - 1);
            return {
                ...state,
                globalSpinnerCount,
                // Clear the message only once no loaders remain active.
                globalSpinnerMessage: globalSpinnerCount === 0 ? undefined : state.globalSpinnerMessage
            };
        }
        /**
         * Following are the error cases that are handled globally
         * and will be shown in the snackbar.
         * These errors are not specific to any action and can occur at any point in the application.
         * The errorDetails will be set in the state and can be accessed by the GlobalErrorHandler component.
         * The GlobalErrorHandler component will show the error message in a snackbar.
         */
        case Actions.GLOBAL_ERROR: {
            return {
                ...state,
                errorDetails: {
                    message: payload.message,
                    label: ErrorMessage.GENERIC_ERROR_MESSAGE,
                    type: ErrorType.ERROR
                }
            };
        }
        case Actions.FETCH_DEALS_FAILURE: {
            return {
                ...state,
                errorDetails: {
                    message: payload?.message || 'Failed to fetch deals',
                    label: ErrorMessage.FETCH_DEALS_FAILURE_MESSAGE,
                    type: ErrorType.ERROR
                }
            };
        }

        case bootstrapActionTypes.BOOTSTRAP_FAILURE: {
            return {
                ...state,
                errorDetails: {
                    message: payload.message,
                    label: ErrorMessage.BOOTSTRAP_CONFIG_FAILURE_MESSAGE,
                    type: ErrorType.ERROR
                }
            };
        }
        case Actions.UPDATE_DEAL_FAILURE: {
            return {
                ...state,
                errorDetails: {
                    message: payload.message,
                    label: ErrorMessage.UPDATE_DEAL_FAILURE_MESSAGE,
                    type: ErrorType.ERROR
                }
            };
        }

        case initActionTypes.INIT_PENCIL_SUCCESS: {
            if (payload.dealError) {
                return {
                    ...state,
                    errorDetails: {
                        message: 'No deal associated with this dealXgId',
                        label: ErrorMessage.BOOTSTRAP_CONFIG_FAILURE_MESSAGE,
                        type: ErrorType.INFO
                    }
                };
            }
            if (payload.dealerDetailsError) {
                return {
                    ...state,
                    errorDetails: {
                        message: payload.dealerDetailsError.message || 'Failed to fetch dealership details',
                        label: ErrorMessage.BOOTSTRAP_CONFIG_FAILURE_MESSAGE,
                        type: ErrorType.ERROR
                    }
                };
            }
            return {
                ...state
            };
        }
        case Actions.UPDATE_DEAL_SUCCESS: {
            if (!action.meta?.sentToDesk) {
                return state;
            }
            return {
                ...state,
                errorDetails: {
                    message: SEND_TO_DESK_SUCCESS_MESSAGE,
                    type: ErrorType.INFO
                }
            };
        }
        case Actions.TRADE_IN_UPDATE_SUCCESS: {
            return {
                ...state,
                errorDetails: {
                    message: 'Trade-In information updated successfully',
                    type: ErrorType.INFO
                }
            };
        }
        case Actions.TRADE_IN_UPDATE_FAILURE: {
            return {
                ...state,
                errorDetails: {
                    message: 'Trade-In information updated failed',
                    label: ErrorMessage.TRADE_IN_UPDATE_FAILURE_MESSAGE,
                    type: ErrorType.ERROR
                }
            };
        }

        case Actions.GET_DEALER_DETAILS_FAILURE: {
            return {
                ...state,
                errorDetails: {
                    message: action.payload.message,
                    label: ErrorMessage.DEALER_DETAILS_FAILURE_MESSAGE,
                    type: ErrorType.ERROR
                }
            };
        }

        case Actions.TRADE_IN_REMOVE_SUCCESS: {
            return {
                ...state,
                errorDetails: {
                    message: 'Trade-In information removed successfully',
                    type: ErrorType.INFO
                }
            };
        }
        case Actions.TRADE_IN_REMOVE_FAILURE: {
            return {
                ...state,
                errorDetails: {
                    message: 'Trade-In information removed failed',
                    label: ErrorMessage.TRADE_IN_REMOVE_FAILURE_MESSAGE,
                    type: ErrorType.ERROR
                }
            };
        }
        case START_ROLE_ASSUMPTION: {
            const dealXgId = getUrlSearchParams('dealXgId');
            const mode = dealXgId ? salesCheckInMode.VinCRM : salesCheckInMode.Standalone;
            sessionStorage.setItem(SALES_CHECKIN_MODE, mode);
            return {
                ...state,
                salesCheckInMode: mode
            };
        }

        case Actions.UPDATE_TRADE_IN_CURRENT_LOCATION: {
            const { nextRoute } = action.payload;
            return {
                ...state,
                navigateToPath: nextRoute
            };
        }
        case Actions.APPLY_TRADE_IN_EQUIPMENT_OPTIONS: {
            return {
                ...state,
                navigateToPath: '/tradeSummary'
            };
        }

        case Actions.ADD_DEEPLINK_URL_SUCCESS: {
            const updatedDeeplinkUrls = new Map<DeepLinks, string>(state.deeplinkUrls);
            if (payload.docUploadDeepLinkUrl) updatedDeeplinkUrls.set(DeepLinks.DocUpload, payload.docUploadDeepLinkUrl);
            if (payload.creditAppUrl) updatedDeeplinkUrls.set(DeepLinks.CreditApp, payload.creditAppUrl);

            // Only update the deeplinkUrls Map, NOT customerCardOptions
            // customerCardOptions should only be set when user clicks dropdown (via setCustomerCardStateAction)
            return {
                ...state,
                deeplinkUrls: updatedDeeplinkUrls
            };
        }

        case Actions.CDLV3_DATA_READY:
        default: {
            return state;
        }
    }
};
